import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { PieChart } from '@app/core/shared/models/charts';
import { PipelineEventDataReportData } from '@app/pipelines/models/pipeline-event-data-report';
import { environment } from '@environment/environment';
import { combineLatest, Observable, ReplaySubject, share, take, tap } from 'rxjs';
import { SignalBroadcastChannelService } from './signal-broadcast-channel.service';

interface FieldViewChartFilters {
  pipelineId: number;
  kpFrom: number;
  kpTo: number;
  pipelineSurveyIds: number[];
  bathyIds: number[];
  showAnomalies: false; // for CP
}

export interface PipelineFieldViewDepthChartDto {
  surveyLayerData: PipelineEventDataReportData[];
  bathyLayerData: PipelineEventDataReportData[];
}

@Injectable({
  providedIn: 'root'
})
export class FieldViewChartService {
  private readonly http = inject(HttpClient);
  private readonly broadcastService = inject(SignalBroadcastChannelService);

  private static readonly CHANNELS = {
    chartFilters: 'field-view-chart-filters',
    chartDataIsLoading: 'field-view-chart-data-is-loading'
  };

  private readonly _defaultFilters: FieldViewChartFilters = {
    pipelineId: -1,
    kpFrom: 0,
    kpTo: 0,
    pipelineSurveyIds: [],
    bathyIds: [],
    showAnomalies: false
  };

  private readonly _fieldViewChartFilters: WritableSignal<FieldViewChartFilters>;
  private readonly _broadcastFieldViewChartFilters: (value: FieldViewChartFilters) => void;

  private readonly _fieldViewChartDataIsLoading: WritableSignal<boolean>;
  private readonly _broadcastFieldViewDataIsLoading: (value: boolean) => void;

  private readonly _siteAnomalyChartData = signal<PieChart>({ totalCount: 0, pieChartData: [] });
  private readonly _depthProfileChartData = signal<PipelineFieldViewDepthChartDto>({
    bathyLayerData: [],
    surveyLayerData: []
  });
  private readonly _cpProfileChartData = signal<PipelineEventDataReportData[]>([]);
  private readonly _eventBreakdownChartData = signal<PieChart>({ totalCount: 0, pieChartData: [] });

  private readonly cache: { [key: string]: Observable<any> } = {};

  readonly baseUrl = environment.api;

  constructor() {
    {
      const { signal, broadcastUpdate } =
        this.broadcastService.createSynchronizedSignal<FieldViewChartFilters>(
          FieldViewChartService.CHANNELS.chartFilters,
          this._defaultFilters
        );
      this._fieldViewChartFilters = signal;
      this._broadcastFieldViewChartFilters = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewChartService.CHANNELS.chartDataIsLoading,
        true
      );
      this._fieldViewChartDataIsLoading = signal;
      this._broadcastFieldViewDataIsLoading = broadcastUpdate;
    }

    effect(() => {
      const filters = this._fieldViewChartFilters();
      if (filters.pipelineId === -1) {
        return;
      }
      combineLatest([
        this.fetchChartData<PieChart>(filters, 'siteAnomaliesChartData'),
        this.fetchChartData<PipelineFieldViewDepthChartDto>(filters, 'depthProfileChartData'),
        this.fetchChartData<PipelineEventDataReportData[]>(filters, 'pipelineCpChartReport'),
        this.fetchChartData<PieChart>(filters, 'SurveyEventsChartData')
      ]).subscribe(
        ([siteAnomaliesChartData, depthProfileChartData, cpProfileChartData, eventBreakdownChartData]) => {
          this._siteAnomalyChartData.set(siteAnomaliesChartData);
          this._depthProfileChartData.set(depthProfileChartData);
          this._cpProfileChartData.set(this.addSectionBreak(cpProfileChartData));
          this._eventBreakdownChartData.set(eventBreakdownChartData);
        }
      );
    });
  }

  get fieldViewChartFilters(): Signal<FieldViewChartFilters> {
    return this._fieldViewChartFilters.asReadonly();
  }
  get fieldViewChartDataIsLoading(): Signal<boolean> {
    return this._fieldViewChartDataIsLoading.asReadonly();
  }
  get siteAnomalyChartData(): Signal<PieChart> {
    return this._siteAnomalyChartData.asReadonly();
  }
  get depthProfileChartData(): Signal<PipelineFieldViewDepthChartDto> {
    return this._depthProfileChartData.asReadonly();
  }

  get cpProfileChartData(): Signal<PipelineEventDataReportData[]> {
    return this._cpProfileChartData.asReadonly();
  }

  get eventBreakdownChartData(): Signal<PieChart> {
    return this._eventBreakdownChartData.asReadonly();
  }

  setFieldViewChartDataIsLoading(value: boolean): void {
    this._fieldViewChartDataIsLoading.set(value);
    this._broadcastFieldViewDataIsLoading(value);
  }

  updateFieldViewChartFilters(updatedFilters: Partial<FieldViewChartFilters>): void {
    const newFilters = { ...this._fieldViewChartFilters(), ...updatedFilters };
    this._fieldViewChartFilters.set(newFilters);
    this._broadcastFieldViewChartFilters(newFilters);
  }

  private addSectionBreak(filteredRes: PipelineEventDataReportData[]): PipelineEventDataReportData[] {
    const sectionIds = [...new Set(filteredRes.map(value => value.surveySectionId))];
    let index;
    sectionIds.forEach(sectionId => {
      const survey = filteredRes
        .filter(survey1 => survey1.surveySectionId === sectionId)
        .map(value => value.pipelineSurveyId)[0];
      index = filteredRes.map(value1 => value1.surveySectionId).lastIndexOf(sectionId);
      filteredRes.splice(index + 1, 0, {
        pipelineEventDataId: null,
        surveyIdent: null,
        surveyYear: null,
        pipelineSurveyId: survey,
        surveySectionId: null,
        kp: null,
        eventTimestamp: null,
        easting: null,
        northing: null,
        eventNumberValue: null,
        depth: null,
        eventReference: null,
        description: null,
        lengthM: null,
        eventDataDescription: null
      });
    });
    return filteredRes;
  }

  private fetchChartData<T>(filters: FieldViewChartFilters, endpoint: string): Observable<T> {
    this.setFieldViewChartDataIsLoading(true);
    const cacheId = `${endpoint}-${filters.pipelineId}-${filters.kpFrom}-${filters.kpTo}`;
    let requestUrl: string;
    if (!this.cache[cacheId]) {
      if (endpoint === 'pipelineCpChartReport') {
        requestUrl = `${this.baseUrl}pipelineEventData/pipelineCpChartReport`;
        filters = { ...filters, showAnomalies: false };
      } else {
        requestUrl = `${this.baseUrl}pipeline/${filters.pipelineId}/${endpoint}`;
      }

      return this.http.post<T>(requestUrl, filters).pipe(
        share({
          connector: () => new ReplaySubject(1),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false
        }),
        take(1),
        tap(() => this.setFieldViewChartDataIsLoading(false))
      );
    }
    this.setFieldViewChartDataIsLoading(false);
    return this.cache[cacheId];
  }

  resetAllToDefault(): void {
    this._fieldViewChartDataIsLoading.set(true);
    this._fieldViewChartFilters.set(this._defaultFilters);
    this._cpProfileChartData.set([]);
    this._siteAnomalyChartData.set({ totalCount: 0, pieChartData: [] });
    this._depthProfileChartData.set({
      bathyLayerData: [],
      surveyLayerData: []
    });
    this._eventBreakdownChartData.set({ totalCount: 0, pieChartData: [] });
  }
}
